import { For, Show } from "solid-js";

import { AccountPicker } from "~/components/AccountPicker";
import { ContactPicker } from "~/components/ContactPicker";
import { CustomRecordPicker } from "~/components/CustomRecordPicker";
import { DealPicker } from "~/components/DealPicker";
import { LookupValue } from "~/components/LookupValue";
import { ProductPicker } from "~/components/ProductPicker";
import { UserPicker } from "~/components/UserPicker";
import { useCustomFields, useCustomFieldsForObject } from "~/lib/hooks";
import type { CustomFieldEntityType, CustomFieldType } from "~/lib/types";

type CurrencyValue = { amount: number; currency: string };
type CFValue = string | number | string[] | CurrencyValue | null;

const isCurrencyValue = (v: unknown): v is CurrencyValue =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as CurrencyValue).amount === "number" &&
  typeof (v as CurrencyValue).currency === "string";

function formatCurrency(v: CurrencyValue): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: v.currency,
      maximumFractionDigits: 2,
    }).format(v.amount);
  } catch {
    return `${v.amount} ${v.currency}`;
  }
}

const inputTypeFor = (ft: CustomFieldType): string => {
  switch (ft) {
    case "date":
      return "date";
    case "number":
      return "number";
    case "email":
      return "email";
    case "url":
      return "url";
    case "phone":
      return "tel";
    default:
      return "text";
  }
};

const linkHrefFor = (ft: CustomFieldType, raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null;
  if (ft === "email") return `mailto:${v}`;
  if (ft === "phone") return `tel:${v}`;
  if (ft === "url") return v;
  return null;
};

type Props = {
  wsKey: () => string;
  // Pass exactly one of these to scope which CFDs to render.
  entityType?: CustomFieldEntityType;
  customObjectDefinitionId?: string;
  // Current values keyed by CFD.key — typically the entity's `custom_fields`.
  values: () => Record<string, unknown> | null | undefined;
  // Called whenever the user edits a value. The receiver decides how to stage
  // changes (form state vs. immediate PATCH).
  onChange: (key: string, value: CFValue) => void;
  // When true, render label-less inline form-style; when false, a labeled
  // detail-view layout. Defaults to inline form style.
  variant?: "form" | "view";
};

export function CustomFieldsRenderer(props: Props) {
  // The two hooks below are mutually exclusive at runtime — each gates on
  // its prop being set via `enabled`. SolidJS-Query keeps both registered
  // but only one actually fetches per render. The `.data` getter on the
  // inactive hook returns undefined, which `fieldsData` then ignores.
  const entityFields = useCustomFields(props.wsKey, () => props.entityType);
  const objectFields = useCustomFieldsForObject(props.wsKey, () => props.customObjectDefinitionId);
  const fields = {
    get data() {
      return props.customObjectDefinitionId ? objectFields.data : entityFields.data;
    },
  };

  const handleScalarChange = (key: string, raw: string, fieldType: CustomFieldType) => {
    if (raw === "") {
      props.onChange(key, null);
      return;
    }
    if (fieldType === "number") {
      const n = Number(raw);
      props.onChange(key, Number.isFinite(n) ? n : null);
      return;
    }
    props.onChange(key, raw);
  };

  const handleMultiSelectToggle = (
    key: string,
    option: string,
    checked: boolean,
    current: string[],
  ) => {
    const next = checked
      ? Array.from(new Set([...current, option]))
      : current.filter((v) => v !== option);
    props.onChange(key, next.length > 0 ? next : null);
  };

  const renderViewValue = (raw: unknown): string | null => {
    if (raw === null || raw === undefined || raw === "") return null;
    if (Array.isArray(raw)) {
      return raw.length > 0 ? raw.join(", ") : null;
    }
    return String(raw);
  };

  return (
    <Show when={(fields.data ?? []).length > 0}>
      <div
        class={
          props.variant === "view"
            ? "grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
            : "grid grid-cols-1 md:grid-cols-2 gap-3"
        }
      >
        <For each={(fields.data ?? []).filter((f) => !f.archived_at)}>
          {(f) => {
            const id = `cf-${f.key}`;
            const raw = props.values()?.[f.key];

            if (props.variant === "view") {
              if (f.field_type === "lookup" && f.lookup_entity_type) {
                const rawStr = typeof raw === "string" ? raw : null;
                return (
                  <div>
                    <dt class="text-xs uppercase opacity-50">{f.label}</dt>
                    <dd class="font-medium">
                      <LookupValue
                        wsKey={props.wsKey}
                        target={f.lookup_entity_type}
                        customObjectDefinitionId={f.lookup_custom_object_definition_id}
                        value={rawStr}
                      />
                    </dd>
                  </div>
                );
              }
              if (f.field_type === "currency") {
                return (
                  <div>
                    <dt class="text-xs uppercase opacity-50">{f.label}</dt>
                    <dd class="font-medium">
                      <Show
                        when={isCurrencyValue(raw)}
                        fallback={<span class="opacity-50">—</span>}
                      >
                        {formatCurrency(raw as CurrencyValue)}
                      </Show>
                    </dd>
                  </div>
                );
              }
              if (f.field_type === "rating") {
                const n = typeof raw === "number" ? raw : 0;
                return (
                  <div>
                    <dt class="text-xs uppercase opacity-50">{f.label}</dt>
                    <dd class="font-medium">
                      <Show when={n > 0} fallback={<span class="opacity-50">—</span>}>
                        <span aria-label={`${n} out of 5 stars`} class="text-warning">
                          {"★".repeat(n)}
                          <span class="opacity-30">{"★".repeat(5 - n)}</span>
                        </span>
                      </Show>
                    </dd>
                  </div>
                );
              }
              const display = renderViewValue(raw);
              const linkHref =
                display && typeof raw === "string" ? linkHrefFor(f.field_type, raw) : null;
              return (
                <div>
                  <dt class="text-xs uppercase opacity-50">{f.label}</dt>
                  <dd class="font-medium">
                    <Show
                      when={display !== null && linkHref !== null}
                      fallback={display ?? <span class="opacity-50">—</span>}
                    >
                      <a
                        href={linkHref ?? "#"}
                        class="link link-primary"
                        target={f.field_type === "url" ? "_blank" : undefined}
                        rel={f.field_type === "url" ? "noopener noreferrer" : undefined}
                      >
                        {display}
                      </a>
                    </Show>
                  </dd>
                </div>
              );
            }

            // Form mode — render the kind-appropriate control.
            const currentScalar = (raw ?? "") as string | number;
            const currentMulti = Array.isArray(raw) ? (raw as string[]) : [];

            return (
              <div>
                <label for={id} class="label">
                  <span class="label-text">
                    {f.label}
                    <Show when={f.required}>
                      <span class="text-error ml-1" aria-hidden="true">
                        *
                      </span>
                    </Show>
                  </span>
                </label>

                <Show when={f.field_type === "multi_select"}>
                  <div class="flex flex-wrap gap-2 border border-base-300 rounded-md p-2 bg-base-100">
                    <For each={f.options ?? []}>
                      {(opt) => (
                        <label class="cursor-pointer flex items-center gap-2">
                          <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            checked={currentMulti.includes(opt)}
                            onChange={(e) =>
                              handleMultiSelectToggle(
                                f.key,
                                opt,
                                e.currentTarget.checked,
                                currentMulti,
                              )
                            }
                          />
                          <span class="text-sm">{opt}</span>
                        </label>
                      )}
                    </For>
                  </div>
                </Show>

                <Show when={f.field_type === "select"}>
                  <select
                    id={id}
                    class="select select-bordered w-full"
                    value={currentScalar === null ? "" : String(currentScalar)}
                    onChange={(e) => handleScalarChange(f.key, e.currentTarget.value, "select")}
                    required={f.required}
                    aria-required={f.required}
                  >
                    <Show when={!f.required}>
                      <option value="">—</option>
                    </Show>
                    <For each={f.options ?? []}>{(opt) => <option value={opt}>{opt}</option>}</For>
                  </select>
                </Show>

                <Show when={f.field_type === "currency"}>
                  {(() => {
                    const cur = isCurrencyValue(raw) ? raw : { amount: 0, currency: "" };
                    const commit = (next: Partial<CurrencyValue>) => {
                      const merged: CurrencyValue = {
                        amount: next.amount ?? cur.amount,
                        currency: (next.currency ?? cur.currency).trim().toUpperCase(),
                      };
                      // Both fields required for a valid currency value —
                      // emit null until both are present so the validator
                      // doesn't reject on the half-typed shape.
                      if (merged.currency.length !== 3 || Number.isNaN(merged.amount)) {
                        props.onChange(f.key, null);
                        return;
                      }
                      props.onChange(f.key, merged);
                    };
                    return (
                      <div class="grid grid-cols-3 gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          class="input input-bordered col-span-2"
                          placeholder="Amount"
                          aria-label={`${f.label} amount`}
                          value={cur.amount || ""}
                          onInput={(e) => commit({ amount: Number(e.currentTarget.value) })}
                        />
                        <input
                          class="input input-bordered uppercase"
                          placeholder="USD"
                          maxlength="3"
                          aria-label={`${f.label} currency code`}
                          value={cur.currency}
                          onInput={(e) => commit({ currency: e.currentTarget.value })}
                        />
                      </div>
                    );
                  })()}
                </Show>

                <Show when={f.field_type === "rating"}>
                  <div class="flex items-center gap-1" role="radiogroup" aria-label={f.label}>
                    <For each={[1, 2, 3, 4, 5]}>
                      {(n) => (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={Number(currentScalar) === n}
                          aria-label={`${n} star${n === 1 ? "" : "s"}`}
                          class={`btn btn-sm btn-square ${
                            Number(currentScalar) >= n
                              ? "btn-warning"
                              : "btn-ghost text-base-content/30"
                          }`}
                          onClick={() =>
                            // Click the current rating to clear it; otherwise
                            // set to N. Matches Gmail/Trello-style toggle.
                            props.onChange(f.key, Number(currentScalar) === n ? null : n)
                          }
                        >
                          ★
                        </button>
                      )}
                    </For>
                    <Show when={Number(currentScalar) > 0 && !f.required}>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm ml-2"
                        onClick={() => props.onChange(f.key, null)}
                      >
                        Clear
                      </button>
                    </Show>
                  </div>
                </Show>

                <Show when={f.field_type === "lookup" && f.lookup_entity_type === "account"}>
                  <AccountPicker
                    wsKey={props.wsKey}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>
                <Show when={f.field_type === "lookup" && f.lookup_entity_type === "contact"}>
                  <ContactPicker
                    wsKey={props.wsKey}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>
                <Show when={f.field_type === "lookup" && f.lookup_entity_type === "user"}>
                  <UserPicker
                    wsKey={props.wsKey}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>
                <Show when={f.field_type === "lookup" && f.lookup_entity_type === "product"}>
                  <ProductPicker
                    wsKey={props.wsKey}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>
                <Show when={f.field_type === "lookup" && f.lookup_entity_type === "deal"}>
                  <DealPicker
                    wsKey={props.wsKey}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>
                <Show
                  when={
                    f.field_type === "lookup" &&
                    f.lookup_entity_type === "custom_record" &&
                    f.lookup_custom_object_definition_id
                  }
                >
                  <CustomRecordPicker
                    wsKey={props.wsKey}
                    customObjectDefinitionId={() => f.lookup_custom_object_definition_id as string}
                    value={() => (typeof raw === "string" ? raw : null)}
                    onChange={(id) => props.onChange(f.key, id)}
                    placeholder={`Search ${f.label.toLowerCase()}…`}
                    label={f.label}
                  />
                </Show>

                <Show
                  when={
                    f.field_type !== "select" &&
                    f.field_type !== "multi_select" &&
                    f.field_type !== "lookup" &&
                    f.field_type !== "rating" &&
                    f.field_type !== "currency"
                  }
                >
                  <input
                    id={id}
                    type={inputTypeFor(f.field_type)}
                    class="input input-bordered w-full"
                    value={currentScalar === null ? "" : String(currentScalar)}
                    onInput={(e) => handleScalarChange(f.key, e.currentTarget.value, f.field_type)}
                    required={f.required}
                    aria-required={f.required}
                  />
                </Show>

                <Show when={f.help_text}>
                  <div class="text-xs opacity-60 mt-1">{f.help_text}</div>
                </Show>
              </div>
            );
          }}
        </For>
      </div>
    </Show>
  );
}
